import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Maximize, Minimize, Grid3X3, StickyNote } from "lucide-react";
import TitleSlide from "@/components/slides/TitleSlide";
import MediaSlide from "@/components/slides/MediaSlide";
import SmartArtSlide from "@/components/slides/SmartArtSlide";
import ComparisonSlide from "@/components/slides/ComparisonSlide";
import GraphSlide from "@/components/slides/GraphSlide";
import CaseStudySlide from "@/components/slides/CaseStudySlide";
import TimelineSlide from "@/components/slides/TimelineSlide";
import TestimonialSlide from "@/components/slides/TestimonialSlide";
import ClosingSlide from "@/components/slides/ClosingSlide";
import SEO from "@/components/SEO";

const slides = [
  {
    id: 1, component: TitleSlide, label: "Title",
    notes: "Welcome everyone. EastWest IT is an independent Microsoft 365 advisory focused on Copilot, AI agents, governance and security. Set the scene: AI is no longer optional — organisations need a trusted partner to deploy it safely.",
  },
  {
    id: 2, component: MediaSlide, label: "Meet the Advisor",
    notes: "Introduce Jesper West — 15+ years in enterprise IT, specialising in Microsoft 365 ecosystem. Highlight cross-industry experience across Nordics and EMEA. Emphasise vendor-independent advice.",
  },
  {
    id: 3, component: SmartArtSlide, label: "Service Pillars",
    notes: "Walk through the six pillars. Each pillar is a standalone engagement or part of a holistic programme. Stress that governance is not a blocker — it's an accelerator for safe AI adoption.",
  },
  {
    id: 4, component: ComparisonSlide, label: "Without vs. With",
    notes: "Contrast the reality of unguided AI adoption vs. a structured approach with EastWest IT. Use this to create urgency. Shadow AI is already happening — the question is whether it's governed.",
  },
  {
    id: 5, component: GraphSlide, label: "Data & Analytics",
    notes: "Show concrete data: adoption rates grow steadily when paired with governance. ROI is measurable from month one. Pie chart shows balanced investment across capabilities.",
  },
  {
    id: 6, component: CaseStudySlide, label: "Case Study",
    notes: "Real-world example of enterprise transformation. 340% ROI, 2,400+ users, 12hrs saved per person per month. Walk through the key outcomes — especially the 90% reduction in shadow-AI risk.",
  },
  {
    id: 7, component: TimelineSlide, label: "Roadmap",
    notes: "Present the typical 10-week engagement model. Phase 1: Discovery. Phase 2: Governance framework. Phase 3: Pilot deployment. Phase 4: Scale and optimise. Each phase has clear deliverables.",
  },
  {
    id: 8, component: TestimonialSlide, label: "Testimonials",
    notes: "Let client voices build credibility. The CTO quote emphasises speed-to-value. The Head of Digital quote highlights board-level confidence through governance. Pause and let these land.",
  },
  {
    id: 9, component: ClosingSlide, label: "Closing",
    notes: "Call to action. Invite the audience to schedule a discovery call. Share email: jesper@eastwestit.dk. Reiterate the core promise: AI that is governed, secure, and creates measurable value.",
  },
];

const SlideDeck = () => {
  const [current, setCurrent] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [direction, setDirection] = useState(1);

  const goTo = useCallback((index: number) => {
    setDirection(index > current ? 1 : -1);
    setCurrent(index);
    setShowGrid(false);
  }, [current]);

  const next = useCallback(() => {
    if (current < slides.length - 1) {
      setDirection(1);
      setCurrent(c => c + 1);
    }
  }, [current]);

  const prev = useCallback(() => {
    if (current > 0) {
      setDirection(-1);
      setCurrent(c => c - 1);
    }
  }, [current]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); next(); }
      if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
      if (e.key === "Escape" && showGrid) setShowGrid(false);
      if (e.key === "g" || e.key === "G") setShowGrid(g => !g);
      if (e.key === "f" || e.key === "F") toggleFullscreen();
      if (e.key === "n" || e.key === "N") setShowNotes(n => !n);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [next, prev, showGrid, toggleFullscreen]);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const SlideComponent = slides[current].component;

  const variants = {
    enter: (dir: number) => ({ x: dir > 0 ? "100%" : "-100%", opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir: number) => ({ x: dir > 0 ? "-100%" : "100%", opacity: 0 }),
  };

  if (showGrid) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-display text-2xl text-foreground">All Slides</h1>
          <button onClick={() => setShowGrid(false)} aria-label="Back to presentation" className="text-muted-foreground hover:text-foreground transition-colors text-sm">
            ← Back to presentation
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
          {slides.map((slide, i) => (
            <button
              key={slide.id}
              onClick={() => goTo(i)}
              aria-label={`Go to slide ${i + 1}: ${slide.label}`}
              className={`relative aspect-video rounded-lg overflow-hidden border-2 transition-all hover:scale-[1.03] ${
                i === current ? "border-primary shadow-lg shadow-primary/20" : "border-border hover:border-primary/50"
              }`}
            >
              <div className="w-full h-full pointer-events-none scale-100">
                <slide.component thumbnail />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-background/80 backdrop-blur-sm px-3 py-1.5 text-xs font-display text-foreground">
                {i + 1}. {slide.label}
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  const { lang } = useTranslation();
  return (
    <div className="relative w-screen h-screen overflow-hidden bg-background select-none flex flex-col">
      <SEO
        title={lang === "da" ? "Præsentation — EastWestIT" : "Investor & Client Slide Deck — EastWestIT"}
        description={
          lang === "da"
            ? "Interaktiv præsentation: sådan hjælper EastWest IT Microsoft 365-organisationer med at tage Copilot og AI-agenter i brug sikkert og med styr på governance."
            : "Interactive presentation: how EastWest IT helps Microsoft 365 organisations adopt Copilot and AI agents safely with strong governance."
        }
        path="/deck"
      />
      {/* Slide area */}
      <div className={`relative flex-1 ${showNotes ? "h-[70%]" : "h-full"}`}>
        <AnimatePresence custom={direction} mode="wait">
          <motion.div
            key={current}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0"
          >
            <SlideComponent />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Speaker Notes Panel */}
      <AnimatePresence>
        {showNotes && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "30%", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-50 border-t border-border bg-card/95 backdrop-blur-md overflow-hidden"
          >
            <div className="h-full overflow-y-auto p-5 md:p-8">
              <div className="flex items-center gap-3 mb-3">
                <StickyNote size={16} className="text-primary" />
                <p className="font-display text-xs font-semibold text-primary uppercase tracking-wider">
                  Speaker Notes — Slide {current + 1}
                </p>
              </div>
              <p className="text-muted-foreground font-body leading-relaxed text-sm md:text-base max-w-3xl">
                {slides[current].notes}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom controls */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 glass rounded-full px-4 py-2 z-50"
        style={{ bottom: showNotes ? "calc(30% + 1.5rem)" : "1.5rem" }}
      >
        <button onClick={prev} disabled={current === 0} aria-label="Previous slide" className="p-1.5 rounded-full hover:bg-primary/20 disabled:opacity-30 transition-colors">
          <ChevronLeft size={18} className="text-foreground" />
        </button>
        <span className="font-display text-sm text-foreground tabular-nums min-w-[3rem] text-center">
          {current + 1} / {slides.length}
        </span>
        <button onClick={next} disabled={current === slides.length - 1} aria-label="Next slide" className="p-1.5 rounded-full hover:bg-primary/20 disabled:opacity-30 transition-colors">
          <ChevronRight size={18} className="text-foreground" />
        </button>
        <div className="w-px h-5 bg-border mx-1" />
        <button
          onClick={() => setShowNotes(n => !n)}
          aria-label={showNotes ? "Hide speaker notes" : "Show speaker notes"}
          className={`p-1.5 rounded-full transition-colors ${showNotes ? "bg-primary/20" : "hover:bg-primary/20"}`}
          title="Toggle speaker notes (N)"
        >
          <StickyNote size={16} className="text-foreground" />
        </button>
        <button onClick={() => setShowGrid(true)} aria-label="Show all slides overview" className="p-1.5 rounded-full hover:bg-primary/20 transition-colors">
          <Grid3X3 size={16} className="text-foreground" />
        </button>
        <button onClick={toggleFullscreen} aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"} className="p-1.5 rounded-full hover:bg-primary/20 transition-colors">
          {isFullscreen ? <Minimize size={16} className="text-foreground" /> : <Maximize size={16} className="text-foreground" />}
        </button>
      </motion.div>

      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-border/30 z-50">
        <motion.div
          className="h-full"
          style={{ background: "var(--gradient-primary)" }}
          animate={{ width: `${((current + 1) / slides.length) * 100}%` }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        />
      </div>
    </div>
  );
};

export default SlideDeck;
